'use strict';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { ajax, get } from 'jquery';
import { default as setDescendantProp } from 'lodash.set';
import { default as getDescendantProp } from 'lodash.get';

import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { useForm } from 'react-hook-form';
import { FormCheck } from 'react-bootstrap';
import { formatPhone } from './utils.js';

import ZohoCustomerSearch from './ZohoCustomerSearch.jsx';
import {Context} from './StockManagerContext';
import PostcodeAutocomplete from './PostcodeAutocomplete.jsx';

const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
}

const isValidAustralianPhone = (phone) => {
    const re = /^(?:\+?61|0)[2-478](?:[ -]?\d){8}$/;
    return re.test(phone);
}
const isValidNZPhone = (phone) => {
    const re = /^(\(0[2-9][0-9]\)|0[2-9][0-9]|0800|0508|0900)[\s-]?\d{3,4}[\s-]?\d{3,4}$/;
    return re.test(phone);
}

// TODO - Move to SWR
const cache = {};
const cachedGet = async url => {
    if (!cache[url]) {
        cache[url] = await get(url);
    }

    return cache[url];
};

cachedGet('/api/zoho/contact-model');
cachedGet('/api/zoho/users');

// Custom JSON POST function for jQuery. jQuery turns body into URL params automatically.
const post = function (url, body) {
    return ajax({
        type: 'POST',
        url: url,
        data: JSON.stringify(body),
        contentType: "application/json",
        dataType: 'json'
    });
}

const fields = {
    'lead-owner': {
        type: 'select',
        label: 'Lead Owner',
        required: true,
        options: 'users',
        zohoContact: 'Owner.id',
        sorted: true
    },
    'sales-lead-from': {
        type: 'select',
        required: true,
        label: 'Sales Lead From',
        zohoContact: 'Sales_Lead_From',
        zohoLead: 'Sales_Lead_From1',
        options: 'contactModel'
    },
    'contact-mobile': {
        type: 'phone',
        label: 'Customer Phone',
        required: true,
        neto: 'ShippingAddress.ShipPhone',
        zohoContact: 'Mobile',
    },
    'contact-email': {
        type: 'email',
        label: 'Customer Email',
        required: true,
        requiredLead: false,
        neto: 'EmailAddress',
        zohoContact: 'Email'
    },
    'contact-first-name': {
        type: 'text',
        label: 'Contact First Name',
        required: true,
        neto: 'BillingAddress.BillFirstName',
        zohoContact: 'First_Name'
    },
    'contact-last-name': {
        type: 'text',
        label: 'Contact Last Name',
        required: true,
        neto: 'BillingAddress.BillLastName',
        zohoContact: 'Last_Name'
    },
    'opt_out': {
        type: 'checkbox',
        label: 'Opt-Out of Email Marketing',
        value: true,
        neto: 'NewsletterSubscriber',
        zohoContact: 'Email_Opt_Out'
        // Note: Opting back in is not possible right now. The "false" value is not submitted to Zoho
    },
    // Alternate contacts
    'alt-contact-spouse-name': {
        type: 'text',
        label: 'Spouse Name',
        required: false,
        zohoContact: 'Spouse',
        zohoLead: 'Spouse_Name'
    },
    'alt-contact-secondary-email': {
        type: 'email',
        label: 'Secondary Email',
        required: false,
        neto: 'SecondaryEmailAddress',
        zohoContact: 'Secondary_Email',
    },
    'alt-contact-phone': {
        type: 'phone',
        label: 'Phone',
        required: false,
        neto: 'BillingAddress.BillPhone',
        zohoContact: 'Phone',
    },
    'alt-contact-extension': {
        type: 'text',
        label: 'Extension',
        required: false,
        zohoContact: 'Extension',
    },
    'alt-contact-additional-phone': {
        type: 'phone',
        label: 'Additional Phone',
        required: false,
        zohoContact: 'Additional_Phone',
    },
    'alt-contact-additional-extension': {
        type: 'text',
        label: 'Additional Phone Extension',
        required: false,
        zohoContact: 'Additional_Phone_Extension',
        zohoLead: 'Additional_Phone_Extersion'
    },
    'product-interest': {
        type: 'select',
        label: 'Product Interest',
        zohoContact: 'Product_Interest',
        options: 'contactModel'
    },
    'delivery-postal-code': {
        type: 'text',
        label: 'Delivery Postal Code',
        neto: 'ShippingAddress.ShipPostCode',
        zohoContact: 'Delivery_Postcode',
        required: true,
        requiredLead: false
    },
    'delivery-street-address-1': {
        type: 'text',
        label: 'Delivery Street',
        neto: 'ShippingAddress.ShipStreetLine1',
        zohoContact: 'Single_Line_15',
        zohoLead: 'Delivery_Street'
    },
    'delivery-street-address-2': {
        type: 'text',
        label: 'Delivery Unit/Apartment Number',
        neto: 'ShippingAddress.ShipStreetLine2',
        zohoContact: 'Single_Line_14',
        zohoLead: 'Street_2'
    },
    'delivery-city': {
        type: 'text',
        label: 'Delivery City',
        neto: 'ShippingAddress.ShipCity',
        zohoContact: 'Delivery_City'
    },
    'delivery-state': {
        type: 'select',
        label: 'Delivery Province',
        neto: 'ShippingAddress.ShipState',
        options: 'contactModel',
        zohoContact: 'Delivery_State',
        sorted: true
    },
    'billing-postal-code': {
        type: 'text',
        label: 'Billing Postal Code',
        neto: 'BillingAddress.BillPostCode',
        zohoContact: 'Billing_Postcode'
    },
    'billing-street-address-1': {
        type: 'text',
        label: 'Billing Street',
        neto: 'BillingAddress.BillStreetLine1',
        zohoContact: 'Billing_Address_Street'
    },
    'billing-street-address-2': {
        type: 'text',
        label: 'Billing Unit/Apartment Number',
        neto: 'BillingAddress.BillStreetLine2',
        zohoContact: 'Billing_Street_2'
    },
    'billing-city': {
        type: 'text',
        label: 'Billing City',
        neto: 'BillingAddress.BillCity',
        zohoContact: 'Billing_Address_City'
    },
    'billing-state': {
        type: 'select',
        label: 'Billing Province',
        neto: 'BillingAddress.BillState',
        options: 'contactModel',
        zohoContact: 'Billing_State_All',
        zohoLead: 'Billing_Address_State',
        sorted: true
    },
};

let contactRequest = false;
const getZohoContact = async email_or_phone => {
    if (!email_or_phone) {
        return false;
    }

    if (contactRequest) {
        contactRequest.abort();
    }

    try {
        contactRequest = get('/api/zoho/search-contacts?exact=true&includeLeads=true&query=' + email_or_phone);
        const existing = await contactRequest;
        return existing[0];

    } catch { }

}

const TYPE_LEAD = 'lead';
const TYPE_CONTACT = 'contact';
const DEFAULT_TYPE = TYPE_LEAD;

const firstBillingFieldKey = Object.keys(fields).find(key => key.startsWith('billing-'));
const firstAltFieldKey = Object.keys(fields).find(key => key.startsWith('alt-'));

export default function CustomerFormZoho({ closeForm }) {
    const { currentCustomer, setCurrentCustomer } = useContext(Context);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState();
    const [contactModel, setContactModel] = useState();
    const [users, setUsers] = useState();
    const [creatingNew, setCreatingNew] = useState(false); // false / LEAD / CONTACT

    const type = currentCustomer ? currentCustomer.Type : (creatingNew||DEFAULT_TYPE);

    // Set different required logic for leads and contacts
    const formFields = type === TYPE_CONTACT ? Object.entries(fields).reduce((acc, [key, field]) => {
        // Contacts
        acc[key] = {
            ...field,
            zoho: field.zohoContact
        };
        return acc;
    }, {}) : Object.entries(fields).reduce((acc, [key, field]) => {
        // Leads
        acc[key] = {
            ...field,
            required: typeof field.requiredLead !== 'undefined' ? field.requiredLead : field.required,
            zoho: typeof field.zohoLead !== 'undefined' ? field.zohoLead : field.zohoContact
        };
        return acc;
    }, {});

    const convertLeadToContact = async _ => {
        setLoading(true);
        const response = await post('/api/zoho/convert-lead', JSON.stringify({ lead_id: currentCustomer.id }));
        setLoading(false);

        console.log(response);
        setCurrentCustomer(false);
        setContactFromEmailOrPhone(currentCustomer.Email || currentCustomer.Mobile);
    };

    const formatZohoContact = data => {
        const zohoContactToUpsert = {
            Email: data['customer_email'],
            'Delivery_Country': __COUNTRY__ === 'NZ' ? 'New Zealand' : 'Australia',
            'Billing_Country': __COUNTRY__ === 'NZ' ? 'New Zealand' : 'Australia',
        };

        Object.entries(formFields).filter(([key, field]) => data[key]).map(([key, field]) => {
            if (!field.zoho) {
                return;
            }

            let value = data[key];
            if (field.type === 'checkbox') {
                value = !!value;
            }

            if (field.type === 'phone' && value) {
                value = formatPhone(value);
            }

            setDescendantProp(zohoContactToUpsert, field.zoho, value);

        });

        return zohoContactToUpsert;
    };

    const formatNetoCustomer = data => {
            const neto = {
                'EmailAddress': data['customer_email'],
                'BillCountry': __COUNTRY__
            }

            Object.entries(formFields).filter(([key, field]) => data[key]).map(([key, field]) => {
                if (!field.neto) {
                    return;
                }

                let value = data[key];
                if (field.type === 'checkbox') {
                    value = !!value;
                }

                if (field.type === 'phone' && value) {
                    value = formatPhone(value);
                }

                if (key === 'opt_out') {
                    value = !value; // Invert value
                }

                setDescendantProp(neto, field.neto, value);
            });

            return neto;

    };

    const upsertCustomer = async (data) => {
        console.log('Upserting customer', data);

        // Upsert in Zoho
        const req = {}
        const zohoCustomerToUpsert = formatZohoContact(data);

        req[type] = zohoCustomerToUpsert;

        // Upsert in Neto
        if (type === TYPE_CONTACT) {
            req['neto'] = formatNetoCustomer(data);
        }
        //return false;
        const response = await post('/api/zoho/upsert-contact', JSON.stringify(req),);

        if (!response.success) {
            alert('Something went wrong saving this contact. Please try again later.')
            return false;
        }

        return true;

    };

    const [isBillingSame, setIsBillingSame] = useState(true); // Default to true (checked)
    const [showAltContact, setShowAltContact] = useState(false); // State for alt contact visibility

    const { register, handleSubmit, watch, errors, setValue } = useForm({
        mode: 'onBlur'
    });

    useEffect(() => {
        get_contact_model();
        get_users();

        async function get_contact_model() {
            const contact_model = await cachedGet('/api/zoho/contact-model');
            setContactModel(contact_model);
        }

        async function get_users() {
            const users = await cachedGet('/api/zoho/users');
            setUsers(users);
        }
    }, []);

    // When customer is changed, reset the form
    useEffect(() => {
        // Reset isBillingSame and showAltContact when the contact context changes
        setIsBillingSame(!currentCustomer);
        setShowAltContact(false); // Reset alt contact visibility

        // Fill in form fields
        if ( currentCustomer ) {
            setTimeout(() => {
                Object.entries(formFields).map(([key, field]) => {
                    if (field.prefill === false) {
                        setValue(key, '');
                    } else {
                        // Check if the field is in the current customer object
                        setValue(key, getDescendantProp(currentCustomer, field.zoho), { shouldTouch: true, shouldDirty: true });
                    }
                });
            });
        }
    }, [currentCustomer]);

    const labelWidth = 6;
    const inputWidth = 12 - labelWidth;

    const onSubmit = async data => {
        setSubmitting(true);

        // Copy delivery address to billing address only for NEW contacts when checkbox is unchecked
        if (!currentCustomer && isBillingSame) {
            data['billing-street-address-1'] = data['delivery-street-address-1'];
            data['billing-street-address-2'] = data['delivery-street-address-2'];
            data['billing-city'] = data['delivery-city'];
            data['billing-state'] = data['delivery-state'];
            data['billing-postal-code'] = data['delivery-postal-code'];
            // Also copy country if needed
            // data['Billing_Country'] = data['Delivery_Country'];
        }

        let result = await upsertCustomer(data);
        if ( !result ) {
            setCurrentCustomer(false);
        } else {
            // Fetch the updated contact from Zoho
            setContactFromEmailOrPhone(data['contact-email'] || data['contact-mobile']);
        }
        setSubmitting(false);
        closeForm();
    };

    const getOptions = (key, field) => {

        const maybeSort = list => field.sorted ? list.sort((a, b) => a.label.localeCompare(b.label)) : list;

        if (field.options === 'users') {

            return maybeSort(users.map(_ => ({
                id: _.id,
                label: _.full_name
            }))) || [];
        }

        if (field.options === 'contactModel') {
            try {

                const zohoField = contactModel.fields.filter(_ => _['api_name'] === field.zohoContact)[0];

                let options = maybeSort(zohoField.pick_list_values.map(_ => ({
                    id: _.actual_value,
                    label: _.display_value
                })));

                if (key.indexOf('state') !== -1) {
                    const testIsLower = /[a-z]/;
                    options = options.filter(option => {
                        const isLower = testIsLower.test(option.label);
                        return __COUNTRY__ === 'NZ' ? !!isLower : !isLower;
                    });
                }

                return options;

            } catch {
                return [];
            }

        }

        if (Array.isArray(field.options)) {
            return maybeSort(field.options.map(_ => ({
                id: _,
                label: _
            })));
        }

        return [];
    };

    const typeaheadRef = useRef(null);

    const clearSearchInput = () => {

        if (typeaheadRef.current) {
            typeaheadRef.current.clear();
        }
    };

    const setContactFromSearch = async contact => {
        if (!contact || (!contact.Email && !contact.Mobile)) {
            return;
        }

        clearSearchInput();

        console.log('Selected user', contact);
        if ( !contact.customOption) {
            // We selected an existing contact or lead from Zoho
            setCurrentCustomer(contact);
            setCreatingNew(false);
            return;
        }

        setContactFromEmailOrPhone(contact.Email);
    }

    const setContactFromEmailOrPhone = async (emailOrPhone) => {
        setCreatingNew(DEFAULT_TYPE);
        setLoading(true);

        // Check zoho if this contact exists...
        const existing = await getZohoContact(emailOrPhone);
        setLoading(false);

        if (existing) {
            setCurrentCustomer(existing);
            setCreatingNew(false);
            return;
        }

        // Prefill the phone or email
        if (emailOrPhone.includes('@')) {
            setValue('contact-email', emailOrPhone);
        } else {
            setValue('contact-mobile', formatPhone(emailOrPhone));
        }
    };

    const back = () => {
        setCreatingNew(false);
        setCurrentCustomer(false);

        // Clear form fields
        Object.entries(formFields).map(([key, field]) => {
            setValue(key, '');
        });
    }

    const validateNewEntry = (options, {text}) => {
        return isValidEmail(text) || isValidAustralianPhone(text) || isValidNZPhone(text);
    };

    const typeLabel = type === TYPE_CONTACT ? 'contact' : 'lead';

    return !submitting && contactModel && users ? <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group as={Row} style={currentCustomer || creatingNew ? { display: 'none' } : {}}>
            <Form.Label column sm={12}>Email or Phone:</Form.Label>
            <Col sm={12}>
                <ZohoCustomerSearch
                    emptyLabel={<div><strong>No matching contacts found.</strong><br/>Enter a valid email or phone number to create a contact or lead.</div>}
                    onSelect={setContactFromSearch}
                    highlightOnlyResults={true}
                    enableSearch={true}
                    allowNew={validateNewEntry}
                    ref={typeaheadRef}
                    newSelectionPrefix={"Create "}
                    includeLeads={true}
                />
            </Col>
        </Form.Group>

        {<div style={creatingNew || currentCustomer ? {} : { display: 'none' }}>
            <Row style={{ marginBottom: 10 }}>
                <Col>
                    <Button variant="secondary" onClick={back}>Back to Search</Button>
                    {currentCustomer && <>
                        {TYPE_LEAD === type && <Button variant="primary" style={{float: 'right'}} onClick={convertLeadToContact}>Convert to Contact</Button>}
                        {TYPE_CONTACT === type && <Button variant="primary" style={{float: 'right'}} onClick={closeForm}>Use Contact</Button>}
                    </>}
                </Col>
            </Row>

            {(currentCustomer && <Alert variant='info'>
                Updating existing {typeLabel}.
            </Alert>)
                ||
                (loading && <Alert variant='light'>
                    <Spinner animation="grow" role="status" size='sm' style={{ marginRight: 10 }}>
                    </Spinner>
                    Double-checking {typeLabel} details in Zoho...
                </Alert>)
                ||
                (<Alert variant='success'>
                    <strong>New {typeLabel}</strong> - please fill in the details below.<br />
                    <small><a href="#" onClick={_ => setCreatingNew(type === TYPE_CONTACT ? TYPE_LEAD : TYPE_CONTACT)}>Switch to {type === TYPE_CONTACT ? 'Lead' : 'Contact'}</a></small>
                </Alert>)}

            {Object.entries(formFields).map(([key, field]) => {
                const isFirstBillingField = !currentCustomer && key === firstBillingFieldKey;
                const billingIsDifferentField = <Form.Group as={Row} key="billing-diff-checkbox-group">
                                                    <Col sm={{ span: inputWidth, offset: labelWidth }}>
                                                        <FormCheck
                                                            type="checkbox"
                                                            id="billing-different-checkbox"
                                                            label="Use delivery address as billing address"
                                                            checked={isBillingSame}
                                                            onChange={(e) => setIsBillingSame(e.target.checked)}
                                                        />
                                                    </Col>
                                                </Form.Group>;

                const isFirstAltField = key === firstAltFieldKey;
                // Use an icon (e.g., simple text or an icon component) and a clickable element
                const altContactToggle = <Form.Group as={Row} key="alt-contact-toggle-group">
                                            <Col>
                                                <div onClick={() => setShowAltContact(!showAltContact)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                                                    <span style={{ marginRight: '5px', fontSize: '1.2em' }}>
                                                        {showAltContact ? '▼' : '▶'} {/* Simple expand/collapse icons */}
                                                    </span>
                                                    {showAltContact ? 'Hide' : 'Show'} alternate contact details
                                                </div>
                                            </Col>
                                        </Form.Group>;

                // Determine if the current field group should be rendered
                const shouldRenderBillingField = !(key.startsWith('billing-') && isBillingSame);
                const shouldRenderAltField = !(key.startsWith('alt-') && !showAltContact);
                const shouldRenderField = shouldRenderBillingField && shouldRenderAltField;

                // Custom rendering for delivery-postal-code and billing-postal-code
                if (__COUNTRY__ === 'AU' && (key.endsWith('postal-code')) && shouldRenderField) {
                    // Determine which city/state fields to update
                    const prefix = key.startsWith('delivery-') ? 'delivery-' : 'billing-';
                    return (
                        <Form.Group as={Row} key={'field_' + key}>
                            <Form.Label column sm={labelWidth}>{field.label}{field.required ? ' *' : ''}</Form.Label>
                            <Col sm={inputWidth}>
                                <PostcodeAutocomplete
                                    value={watch(key)}
                                    onChange={v => setValue(key, v)}
                                    onSelect={({ postcode, city, province }) => {
                                        setValue(key, postcode);
                                        setValue(prefix + 'city', city);
                                        setValue(prefix + 'state', province);
                                    }}
                                    disabled={loading}
                                    required={field.required}
                                    {...register(key, { required: field.required })}
                                />
                            </Col>
                        </Form.Group>
                    );
                }

                return (
                    <React.Fragment key={'fragment_' + key}>
                        {/* Render checkbox before the first billing field */}
                        {isFirstBillingField && billingIsDifferentField}

                        {/* Render toggle before the first alt field */}
                        {isFirstAltField && altContactToggle}

                        {/* Conditionally render the field group */}
                        {<Form.Group as={Row} key={'field_' + key} style={{ display: shouldRenderField ? '' : 'none' }}>
                               <Form.Label column sm={key.startsWith('alt-') ? { span: labelWidth-1, offset: 1 } : labelWidth}>{field.type !== 'checkbox' && field.label}{field.required ? ' *' : ''}</Form.Label>
                                 <Col sm={inputWidth}>
                                    {(field.type === 'select' &&
                                        <Form.Control
                                            as="select"
                                            name={key}
                                            ref={register({
                                                required: field.required // Updated required logic
                                            })}
                                            isInvalid={errors[key]}
                                            disabled={loading}
                                            style={field.required ? { borderLeft: '3px solid #F14949' } : {}} // Style based on effective required status
                                        >
                                             <option value=""></option>
                                             {getOptions(key, field).map(option =>
                                                 <option value={option.id} key={`f-${key}-${option.id}`}>{option.label}</option>
                                             )
                                             }
                                        </Form.Control>)
                                        ||
                                        (field.type === 'checkbox' &&
                                            <Form.Check
                                                id={key}
                                                name={key}
                                                ref={register({
                                                    required: field.required
                                                })}
                                                isInvalid={errors[key]}
                                                disabled={loading}
                                                value={field.value}
                                                label={field.label}
                                            />)
                                        ||
                                        (field.type === 'textarea' &&
                                            <Form.Control
                                                as="textarea"
                                                name={key}
                                                ref={register({
                                                    required: field.required
                                                })}
                                                rows={3}
                                                isInvalid={errors[key]}
                                                disabled={loading}
                                                style={field.required  ? { borderLeft: '3px solid #F14949' } : {}} // Style based on effective required status
                                            />
                                        )
                                        ||
                                        <Form.Control
                                            type={field.type}
                                            name={key}
                                            placeholder=""
                                            ref={register({
                                                required: field.required && (!key.startsWith('billing-') || !isBillingSame),
                                                minLength: field.type === 'phone' ? 10 : false
                                            })}
                                            isInvalid={errors[key]}
                                            disabled={loading}
                                            style={field.required ? { borderLeft: '3px solid #F14949' } : {}} // Style based on effective required status
                                            className={field.required ? 'required' : ''} // Class based on effective required status
                                        />
                                    }
                                </Col>
                            </Form.Group>
                        }
                    </React.Fragment>
                );
            })}


            <Button variant="primary" type="submit" disabled={loading}>
                {currentCustomer ? "Update " + typeLabel : "Create " + typeLabel}
            </Button>
        </div>}

    </Form> : <div>
        <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
        </Spinner> {submitting && `Saving ${typeLabel}...`}
    </div>
}
